/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { createResource } from '@web-stories-wp/media';
/**
 * Internal dependencies
 */
import { PROVIDERS } from '../media3p/providerConfiguration';
import { ProviderType } from '../media3p/constants';

/**
 * Author object.
 *
 * @typedef {Object} Author Media3p media author.
 * @property {string} url The url for the asset.
 * @property {?string} imageName A name to identify this particular image url.
 * Currently not being used.
 * @property {number} width The width of this asset.
 * @property {number} height The height of this asset.
 */

/**
 * ImageUrl object.
 *
 * @typedef {Object} ImageUrl image url object.
 * @property {string} mimeType The mime type of the image.
 * @property {number} width The width of the image.
 * @property {number} height The height of the image.
 * @property {?string} url The url.
 */

/**
 * VideoUrl object.
 *
 * @typedef {Object} VideoUrl video url object.
 * @property {string} mimeType The mime type of the video.
 * @property {number} width The width of the image.
 * @property {number} height The height of the image.
 * @property {?string} url The url.
 */

/**
 * Media3pMedia object.
 *
 * @typedef {Object} Media3pMedia Media3p media object.
 * @property {string} name The name of the media object. Format is
 * 'media/{provider}:{id}.
 * @property {string} provider The provider.
 * @property {string} type The type of media, currently 'IMAGE' or 'VIDEO'.
 * @property {Author} author The metadata about the author of the media object.
 * @property {?string} description A description of the media item.
 * @property {string} createTime The creation time of the media element.
 * @property {string} updateTime The last update time of the media element.
 * @property {?string} registerUsageUrl The optional URL to call when the media
 * element is added to a story.
 * @property {ImageUrl[]|undefined} imageUrls If the media element is an
 * image, an array of urls with different size assets. If the media element is
 * a video, then poster / thumbnail images.
 * @property {VideoUrl[]|undefined} videoUrls If the media element is a
 * video, an array of urls with different fidelity, eg. full or preview.
 */

/**
 * Converts the image urls in a Media3P Media object to the "sizes"
 * object required for a Resource.
 *
 * @param {Media3pMedia} m The Media3P Media object.
 * @return {Object} The array of "sizes"-type objects.
 */
function getImageUrls(m) {
  const type = m.type.toLowerCase();

  if (type !== 'image') {
    throw new Error('Invalid media type.');
  }

  const providerCode = m.provider?.toLowerCase();

  // Storypilot supplies the original image; its thumbnail is optional.
  if (providerCode === ProviderType.STORYPILOT) {
    const full = m.imageUrls?.find((url) => url.type === 'full');
    if (!full?.url) throw new Error('Image details are unavailable.');
    const large = m.imageUrls.find((url) => url.type === 'large') || full;
    const thumbnail =
      m.imageUrls.find((url) => url.type === 'thumbnail') || large;
    return mapImageUrls({
      ...m,
      imageUrls: [
        full,
        { ...large, type: 'large' },
        { ...thumbnail, type: 'thumbnail' },
      ],
    });
  }

  // In case of Mimir, ensure image url encodings
  if (providerCode === ProviderType.MIMIR) {
    m.imageUrls = m.imageUrls.map((x) => {
      return {
        ...x,
        url: ensureEncoding(x.url),
      };
    });
  }

  if (providerCode === ProviderType.DROPBOX) {
    m.imageUrls = ensureDropboxImageVariants(m.imageUrls);
  }

  // The rest of the application expects 3 named "sizes": "full", "large" and
  // "web_stories_thumbnail". We use the biggest as "full", the next biggest
  // as "large", and the smallest as "web_stories_thumbnail". The rest are
  // named according to their size.
  if (m.imageUrls?.length < 3) {
    throw new Error('Invalid number of urls for asset. Need at least 3: ' + m);
  }

  return mapImageUrls(m);
}

function mapImageUrls(m) {
  const getMediaByType = (type) => m.imageUrls.find((x) => x.type === type);

  const originalSize = {
    originalWidth: getMediaByType('full').width || 0,
    originalHeight: getMediaByType('full').height || 0,
  };

  const formatMediaByType = (type) => {
    const imageUrl = m.imageUrls.find((x) => x.type === type);

    return mediaUrlToImageSizeDescription(m, imageUrl, originalSize);
  };

  return {
    full: formatMediaByType('full'),
    large: formatMediaByType('large'),
    web_stories_thumbnail: formatMediaByType('thumbnail'),
  };
}

function ensureDropboxImageVariants(imageUrls = []) {
  const urls = [...imageUrls];
  const full = urls.find((u) => u.type === 'full');
  if (full && !urls.find((u) => u.type === 'large')) {
    urls.push({ ...full, type: 'large' });
  }
  return urls;
}

/**
 * Converts the gif urls in a Media3P Media object to the "sizes"
 * object required for a resource
 *
 * @param {Media3pMedia} m The Media3P Media object
 * @return {Object} The array of "sizes"-type objects.
 */

function getGifUrls(m) {
  if (m.type.toLowerCase() !== 'gif') {
    throw new Error('Invalid media type');
  }

  // The rest of the application expects 3 named "sizes": "full", "large" and
  // "web_stories_thumbnail". We use the biggest as "full", the next biggest
  // as "large", and the smallest as "web_stories_thumbnail". The rest are
  // named according to their size.
  if (m.imageUrls?.length < 3) {
    throw new Error('Invalid number of urls for asset. Need at least 3: ' + m);
  }

  const webmVideoUrls = m.videoUrls.filter(
    ({ mimeType }) => mimeType === 'image/webm'
  );
  const mp4VideoUrls = m.videoUrls.filter(
    ({ mimeType }) => mimeType === 'video/mp4'
  );

  const previewUrl = m.imageUrls
    .filter(({ imageName }) => imageName.endsWith('preview'))
    .map(({ url }) => url)
    .shift();

  return {
    imageUrls: mapImageUrls({
      ...m,
      // Prevents the static preview image from appearing in this list
      // which should only contain animated GIFs.
      imageUrls: m.imageUrls.filter(({ url }) => url !== previewUrl),
    }),
    videoUrls: {
      webm: mapVideoUrls({ ...m, videoUrls: webmVideoUrls }),
      mp4: mapVideoUrls({ ...m, videoUrls: mp4VideoUrls }),
    },
    previewUrl,
  };
}

/**
 * Converts the video urls in a Media3P Media object to the "sizes"
 * object required for a Resource.
 *
 * @param {Media3pMedia} m The Media3P Media object.
 * @return {Object} The array of "sizes"-type objects.
 */
function getVideoUrls(m) {
  if (m.type.toLowerCase() !== 'video') {
    throw new Error('Invalid media type.');
  }

  // Storypilot may supply only the original playable video.
  if (m.provider?.toLowerCase() === ProviderType.STORYPILOT) {
    const full = m.videoUrls?.find((url) => url.type === 'full');
    if (!full?.url) throw new Error('Video details are unavailable.');
    const preview = m.videoUrls.find((url) => url.type === 'thumbnail') || full;
    return mapVideoUrls({
      ...m,
      videoUrls: [full, { ...preview, type: 'thumbnail' }],
    });
  }

  if (m.provider?.toLowerCase() === ProviderType.SCOREPLAY) {
    const valid = (m.videoUrls || []).filter((url) =>
      /^https?:\/\//i.test(url?.url || '')
    );
    const full = valid.find((url) => url.type === 'full') || valid[0];
    const preview = valid.find((url) => url.type === 'thumbnail') || full;
    if (!full) throw new Error('Video details are unavailable.');
    const map = (url) => ({
      ...url,
      file: m.name,
      source_url: url.url,
      mime_type: url.mimeType,
      requestFullUrl: false,
    });
    return { full: map(full), preview: map(preview) };
  }

  // Dropbox videos (and single-URL OpusClip clips) only return a single stream,
  // so we map the same entry to both "full" and "preview" for downstream consumers.
  const providerCode = m.provider.toLowerCase();
  const hasSingleOpusClipUrl =
    providerCode === ProviderType.OPUSCLIP && m.videoUrls?.length === 1;
  if (providerCode === ProviderType.DROPBOX || hasSingleOpusClipUrl) {
    const videoUrl = {
      ...m.videoUrls[0],
      file: m.name,
      source_url: m.videoUrls[0].url,
      mime_type: m.videoUrls[0].mimeType,
    };

    return {
      full: videoUrl,
      preview: videoUrl,
    };
  }

  // The rest of the application expects 2 named "sizes": "full", and "preview"
  // The highest fidelity is used (videoUrls is ordered by fidelity from the backend)
  // as "full", and the lowest as "preview".
  if (m.videoUrls?.length < 2) {
    throw new Error('Invalid number of urls for asset. Need at least 2: ' + m);
  }

  return mapVideoUrls(m);
}

function mapVideoUrls(m) {
  const getMediaByType = (type) => m.videoUrls.find((x) => x.type === type);

  const originalSize = {
    originalWidth: getMediaByType('full').width || 0,
    originalHeight: getMediaByType('full').height || 0,
  };

  const formatMediaByType = (type) => {
    const videoUrl = m.videoUrls.find((x) => x.type === type);
    return mediaUrlToImageSizeDescription(m, videoUrl, originalSize);
  };

  return {
    full: formatMediaByType('full'),
    preview: formatMediaByType('thumbnail'),
  };
}

function mediaUrlToImageSizeDescription(media, url, originalSize) {
  const { originalWidth, originalHeight } = originalSize;
  const sizeIsMissing = (size) => !size && size !== 0;
  if (sizeIsMissing(originalWidth) || sizeIsMissing(originalHeight)) {
    throw new Error('No original size present.');
  }
  const provider = PROVIDERS[media.provider.toLowerCase()];
  if (
    (sizeIsMissing(url.width) || sizeIsMissing(url.height)) &&
    !provider.defaultPreviewWidth
  ) {
    throw new Error('Missing width and height for: ' + media);
  }
  return {
    requestFullUrl: url.requestFullUrl,
    file: media.name,
    source_url: url.url,
    mime_type: url.mimeType,
    width: url.width ?? provider.defaultPreviewWidth,
    height:
      url.height ??
      (provider.defaultPreviewWidth * originalHeight) / originalWidth,
  };
}

function getAttributionFromMedia3p(m) {
  return (
    (m.author || m.registerUsageUrl) && {
      author: m.author && {
        displayName: m.author.displayName,
        url: m.author.url,
      },
      registerUsageUrl: m.registerUsageUrl,
    }
  );
}

export function formatVideoLength(length) {
  const minutes = Math.floor(length / 60);
  const seconds = Math.floor(length % 60);
  return minutes + ':' + seconds.toString().padStart(2, '0');
}

const ensureEncoding = (src) => {
  try {
    const url = new URL(src);

    const encodedParams = new URLSearchParams();
    for (let [key, value] of url.searchParams) {
      encodedParams.append(key, encodeURIComponent(value));
    }

    const ensured = `${url.origin}${url.pathname}?${encodedParams.toString()}`;
    return decodeURI(ensured);
  } catch (error) {
    return src;
  }
};

function getImageResourceFromMedia3p(mediaProviderId, m) {
  const imageUrls = getImageUrls(m);
  return createResource({
    mediaProviderId: mediaProviderId,
    id: m.name,
    baseColor: m.baseColor,
    type: m.type.toLowerCase(),
    mimeType: imageUrls.full.mime_type,
    creationDate: m.createTime,
    updateTime: m.updateTime || m.createTime,
    src: imageUrls.full.source_url,
    width: imageUrls.full.width,
    height: imageUrls.full.height,
    alt: m.description || m.title || m.name,
    local: false,
    isExternal: true,
    isPlaceholder: false,
    sizes: imageUrls,
    attribution: getAttributionFromMedia3p(m),
    title: m.title || m.description,
  });
}

function getVideoResourceFromMedia3p(mediaProviderId, m) {
  const videoUrls = getVideoUrls(m);
  // Durations arrive as '22.5s'; details may omit them.
  const length = parseInt(m.videoMetadata?.duration, 10) || 0;
  const providerCode = m.provider.toLowerCase();
  const isOpusClip = providerCode === ProviderType.OPUSCLIP;
  const previewUrl =
    videoUrls.preview?.requestFullUrl !== true
      ? videoUrls.preview?.source_url
      : undefined;
  return createResource({
    mediaProviderId: mediaProviderId,
    id: m.name,
    baseColor: m.baseColor,
    posterId: m.name,
    type: m.type.toLowerCase(),
    mimeType: videoUrls.full.mime_type,
    creationDate: m.createTime,
    updateTime: m.updateTime || m.createTime,
    src:
      isOpusClip && videoUrls.full.requestFullUrl
        ? previewUrl ?? ''
        : videoUrls.full.source_url,
    width: videoUrls.full.width,
    height: videoUrls.full.height,
    poster: m.imageUrls?.[0]?.url,
    length,
    lengthFormatted: formatVideoLength(length),
    alt: m.description || m.title || m.name,
    local: false,
    noAudio: false,
    isExternal: true,
    isPlaceholder: false,
    isOptimized: true,
    isMuted: false,
    sizes: videoUrls,
    attribution: getAttributionFromMedia3p(m),
    title: m.title || m.description,
  });
}

function getGifResourceFromMedia3p(mediaProviderId, m) {
  const { imageUrls, videoUrls, previewUrl } = getGifUrls(m);
  return createResource({
    mediaProviderId: mediaProviderId,
    id: m.name,
    baseColor: m.baseColor,
    posterId: m.name,
    type: m.type.toLowerCase(),
    mimeType: imageUrls.full.mime_type,
    creationDate: m.createTime,
    updateTime: m.updateTime || m.createTime,
    src: imageUrls.full.source_url,
    width: imageUrls.full.width,
    height: imageUrls.full.height,
    poster: previewUrl,
    alt: m.description || m.title || m.name,
    local: false,
    isPlaceholder: false,
    isOptimized: true,
    isExternal: true,
    sizes: imageUrls,
    output: {
      mimeType: videoUrls.mp4.full.mime_type,
      src: videoUrls.mp4.full.source_url,
    },
    attribution: getAttributionFromMedia3p(m),
    title: m.title || m.description,
  });
}

/**
 * Generates a resource object from a Media3P object from the API.
 *
 * @param {string} mediaProviderId Id of the media provider
 * @param {Media3pMedia} m A Media3P Media object.
 * @return {import('@web-stories-wp/media').Resource} Resource object.
 */
export default function getResourceFromMedia3p(mediaProviderId, m) {
  switch (m.type.toLowerCase()) {
    case 'image':
      return getImageResourceFromMedia3p(mediaProviderId, m);
    case 'video':
      return getVideoResourceFromMedia3p(mediaProviderId, m);
    case 'gif':
      return getGifResourceFromMedia3p(mediaProviderId, m);
    default:
      throw new Error('Invalid media type.');
  }
}
